import { createSelector } from 'reselect';
import type { ITradeIn } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getAllTradeIns } from './tradeIns';

/**
 * Checks whether any meaningful (non-default) trade-in data exists beyond YMMT fields.
 * Used to distinguish "no trade-in on the deal" (pristine reducer state) from
 * "trade-in data was entered but YMMT is missing" (e.g. dealer entered only an allowance).
 *
 * shouldIncludeTradeIn defaults to true in all reducers, so we cannot rely on it alone
 * to determine if a real trade-in exists. Instead we check key data fields against
 * their reducer initial-state defaults.
 *
 * @param tradeIn - Trade-in object to inspect
 * @returns true if any non-YMMT field has moved away from its default value
 */
const hasAnyTradeData = (tradeIn: ITradeIn): boolean => {
    const hasTradeValue = !!tradeIn.value && tradeIn.value > 0;
    const hasPayoff = (!!tradeIn.amountOwed && tradeIn.amountOwed > 0) || (!!tradeIn.owed && tradeIn.owed > 0);
    const hasVin = !!tradeIn.vin && tradeIn.vin !== '';
    const hasMileage = !!tradeIn.mileage && tradeIn.mileage > 0;
    const hasCondition = !!tradeIn.condition && tradeIn.condition !== '';
    const hasAcv =
        !!(tradeIn as unknown as Record<string, unknown>).acv && Number((tradeIn as unknown as Record<string, unknown>).acv) > 0;

    return hasTradeValue || hasPayoff || hasVin || hasMileage || hasCondition || hasAcv;
};

/**
 * Checks if a trade-in vehicle has incomplete YMMT (Year/Make/Model/Trim) information.
 * Year=0 is treated as invalid (sentinel value for unset).
 * 'default' string is treated as invalid (UI placeholder value).
 *
 * @param tradeIn - Trade-in object to validate
 * @returns true if YMMT data is incomplete, false otherwise
 */
const hasIncompleteYmmt = (tradeIn: ITradeIn): boolean => {
    // Check if year, make, model, or trim is missing
    // Per Rally story US1865170 AC#1: "complete year, make, model, and trim (YMMT) information"
    const year = tradeIn.year;
    const make = tradeIn.make;
    const model = tradeIn.model;
    const trim = tradeIn.trim;

    // Consider incomplete if any required field is missing or empty
    // year is number | null - consider 0 or null as invalid
    const isYearInvalid = !year || year === 0;
    // make, model, and trim are string | null - check for empty strings too
    const isMakeInvalid = !make || make === '' || make === 'default';
    const isModelInvalid = !model || model === '' || model === 'default';
    const isTrimInvalid = !trim || trim === '' || trim === 'default';

    // If ALL YMMT fields are empty/default, the trade may either be pristine (initial state)
    // or the dealer entered non-YMMT data (value, payoff, VIN, mileage, condition, ACV).
    // Only flag as incomplete when at least some trade data proves a real trade exists.
    const hasNoYmmtData = isYearInvalid && isMakeInvalid && isModelInvalid && isTrimInvalid;
    if (hasNoYmmtData) {
        return hasAnyTradeData(tradeIn);
    }

    return isYearInvalid || isMakeInvalid || isModelInvalid || isTrimInvalid;
};

/**
 * Checks if any INCLUDED trade-in has incomplete YMMT data for multiple trade-ins.
 * Only checks trade-ins where shouldIncludeTradeIn is true.
 *
 * @param state - Redux state tree
 * @returns true if any included trade-in has incomplete YMMT, false otherwise
 */
export const hasAnyIncludedTradeInWithIncompleteYmmt = createSelector([getAllTradeIns], (allTradeIns): boolean => {
    // Filter to only included (non-deleted and shouldIncludeTradeIn === true)
    const includedTradeIns = allTradeIns.filter((item) => !item.isDeleted && item.tradeIn.shouldIncludeTradeIn);

    // Check if any included trade-in has incomplete YMMT
    return includedTradeIns.some((item) => hasIncompleteYmmt(item.tradeIn));
});

/**
 * Returns array of indices for all INCLUDED trade-ins that have incomplete YMMT.
 * Used to identify which trades need shouldIncludeTradeIn set to false.
 *
 * @param state - Redux state tree
 * @returns array of indices for incomplete trade-ins
 */
export const getIncompleteTradeIndices = createSelector([getAllTradeIns], (allTradeIns): number[] => {
    const incompleteIndices: number[] = [];

    allTradeIns.forEach((item, index) => {
        // Only check included (non-deleted and shouldIncludeTradeIn === true) trade-ins
        if (!item.isDeleted && item.tradeIn.shouldIncludeTradeIn && hasIncompleteYmmt(item.tradeIn)) {
            incompleteIndices.push(index);
        }
    });

    return incompleteIndices;
});

/**
 * Composite selector that determines if the incomplete trade modal should be shown.
 * Combines feature toggle check with YMMT validation for multiple trade-ins.
 *
 * This is the primary selector that should be used by middleware and UI components.
 *
 * @param state - Redux state tree
 * @returns true if feature is enabled AND any included trade-in has incomplete YMMT
 */
export const shouldShowIncompleteTradeModalForMultiple = createSelector(
    [(state: StateTree) => featureToggleSelectors.enableFSRespectTradeInclusion(state), hasAnyIncludedTradeInWithIncompleteYmmt],
    (isFeatureEnabled, hasIncompleteYmmt): boolean => {
        return isFeatureEnabled && hasIncompleteYmmt;
    }
);
